/**
 * @file bmlCommon/pro/layout
 * @description 列表高级布局
 * @author dailin01
 */

import React, {ReactNode} from 'react';
import {Tabs, Button} from 'acud';

import {ACUD_PRO_PREFIX} from '../../../_common/constants';
import PageTitle, {PageTitleBtnProps, PageTitleProps} from '../pageTitle';
import PageLoading, {LoadingProps} from '../../../common/Loading';
import EmptyStatus, {EmptyStatusProps} from '../../EmptyStatus';

import './index.less';

type PageTitleRenderType = (props: any) => React.ReactNode;

interface BackType {
    to?: string;
    backText?: string;
    type?: 'arrow' | 'button';
}

interface btnProps {
    text: ReactNode;
    btnAction?: () => void;
    disabled?: boolean;
}

type TitleProps = PageTitleProps & {
    title?: ReactNode;
    backUrl?: string;
    backOptions?: BackType;
    btnConfig?: PageTitleBtnProps[];
    pageTitleRender?: PageTitleRenderType;
}

interface DescriptionProps {
    description?: ReactNode;
    descLink?: string;
}

// todo aucd TabPane Props
type TabListProps = {
    key: string;
    tab: ReactNode;
    content: ReactNode;
    description?: ReactNode;
    descLink?: string;
}[];

type OnChangeMD = (activeKey: string) => void;

interface TabProps {
    defaultActiveKey?: string;
    activeKey?: string;
    onChange?: OnChangeMD;
    tabRouter?: string;
    history?: any;
}

export type ProLayoutProps = TitleProps & DescriptionProps & {
    // loading
    loading?: boolean;
    loadingProps?: LoadingProps;
    // error
    emptyStatusProps?: EmptyStatusProps;
    // tabs
    tabList?: TabListProps;
    tabInfo?: TabProps;
    prefixCls?: string;
};

const titleRender = (props: TitleProps): React.ReactNode => {
    const {
        title,
        backUrl,
        btnConfig,
        pageTitleRender,
        extra,
        ...rest
    } = props;
    if (pageTitleRender && typeof pageTitleRender === 'function') {
        return pageTitleRender(props);
    }
    // todo
    if (backUrl) {
        return (
            <PageTitle
                backUrl={backUrl}
                title={title}
                btnConfig={btnConfig}
                extra={extra}
                {...rest}
            />
        );
    }
    const isBtnArr = Array.isArray(btnConfig);

    const handleAction = (btnItem: btnProps | PageTitleBtnProps) => {
        const {btnAction} = btnItem;
        btnAction && btnAction();
    };
    return (
        <div>
            <span className="title">{title}</span>
            {isBtnArr && (
                <div className="btns-config">
                    {btnConfig.map(btnItem => (
                        <Button
                            key={btnItem.id}
                            disabled={!!btnItem.disabled}
                            onClick={() => handleAction(btnItem)}
                            type={btnItem.type}
                        >
                            {btnItem.text}
                        </Button>
                    ))
                    }
                </div>
            )}
            {extra && <div className='extra'>{extra}</div>}
        </div>
    );
};

const descRender = (props: DescriptionProps): React.ReactNode => {
    const {description, descLink} = props;
    // 提示警告的文案等
    if (typeof description !== 'string') {
        return <>{description}</>;
    }
    return (
        <div className="desc">
            {description}
            {descLink && (
                <a target="_blank" href={descLink} rel="noreferrer">
                    了解详情 &gt;
                </a>
            )}
        </div>
    );
};

const ProLayout: React.FC<ProLayoutProps> = props => {
    const {
        prefixCls = `${ACUD_PRO_PREFIX}-page-layout-list`,
        title,
        btnConfig,
        backUrl,
        pageTitleRender,
        description,
        descLink,
        tabList,
        tabInfo,
        children,
        loading,
        loadingProps,
        emptyStatusProps,
        extra
    } = props;

    if (emptyStatusProps) {
        return <EmptyStatus {...emptyStatusProps} />;
    }

    let contentDom = <div className="content-area">{children}</div>;

    const titleDom: React.ReactNode = titleRender(
        {
            title,
            btnConfig,
            backUrl,
            extra,
            pageTitleRender
        }
    );

    const descDom = descRender({description, descLink});

    if (Array.isArray(tabList) && tabInfo) {
        contentDom = (
            <div className="content-tab">
                <Tabs {...tabInfo}>
                    {tabList.map(({tab, key, content, description, descLink}) => (
                        <Tabs.TabPane tab={tab} key={key}>
                            {description && (
                                <div className="tab-desc">
                                    {typeof description === 'string' ? (
                                        <>
                                            {description}
                                            {descLink && (
                                                <a target="_blank" href={descLink} rel="noreferrer">
                                                    了解详情 &gt;
                                                </a>
                                            )}
                                        </>
                                    ) : (
                                        <>{description}</>
                                    )}
                                </div>
                            )}
                            <div className="content-area">{content}</div>
                        </Tabs.TabPane>
                    ))}
                </Tabs>
            </div>
        );
    }
    return (
        <div className={`${prefixCls}`}>
            <div className="header-area">
                {titleDom}
                {descDom}
            </div>
            {loading ? <PageLoading {...loadingProps} /> : contentDom}
        </div>
    );
};

export {ProLayout};
